import React, { useState } from 'react'

import styled from '@emotion/styled'
import { useForm } from 'react-hook-form'

import { Button } from '@cash-design-system/react'
import { InvestmentEntitySelectionBlocker } from '@cash-web/protos/squareup/franklin/blockers/investment_entity_selection_blocker.pb'
import { ResponseContext } from '@cash-web/protos/squareup/franklin/ResponseContextProto.pb'
import { useQueryGetDiscovery } from '@cash-web/shared-data-access-api/react-query/useQueryGetDiscovery'
import { InvestmentList } from '@cash-web/shared-feature-investment-list'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

import { BaseBlocker, BaseBlockerFormFields, BlockerLoading, BlockerProps } from '..'
import { useQueryGetAllInvestmentEntities } from '../BlockerInstrumentSelection/useQueryGetAllInvestmentEntities'

interface Props extends BlockerProps {
  blocker: InvestmentEntitySelectionBlocker
  submitInvestment: (entity: string) => Promise<{
    response_context?: ResponseContext
  }>
  submitMultipleInvestments: (entities: Array<string>) => Promise<{
    response_context?: ResponseContext
  }>
}

const Container = styled.div`
  height: 50vh;
  margin: 0 -${({ theme }) => theme.spacing.l};
  overflow-y: scroll;
  padding: 0 ${({ theme }) => theme.spacing.l};
`

export function InvestmentEntitySelection({
  blocker: { stock_selector_screen, multi_selection_parameters },
  submitInvestment,
  submitMultipleInvestments,
}: Props): React.ReactElement {
  const flow = useFlowContext()

  const isMultiSelect = Boolean(multi_selection_parameters)
  const [selectedTokensForMultiSelect, setSelectedTokensForMultiSelect] = useState(
    new Set(multi_selection_parameters?.selected_investment_entity_tokens)
  )

  const { allInvestmentEntities, isLoading } = useQueryGetAllInvestmentEntities()

  const { data: discoveryData, isLoading: discoveryDataLoading } = useQueryGetDiscovery({
    queryOptions: {},
  })

  let { categories = [] } = discoveryData ?? {}
  if (isMultiSelect) {
    const selectedCategory = {
      name: 'Selected',
      showIfEmpty: true,
      investment_entities: Array.from(selectedTokensForMultiSelect).map(token => {
        return {
          investment_entity_token: token,
        }
      }),
    }
    categories = [selectedCategory, ...categories]
  }

  const form = useForm<BaseBlockerFormFields>({ mode: 'onTouched' })

  const { search_bar_placeholder_text, title } = stock_selector_screen || {}
  const submitInvestmentForSingleSelect = (investment: string) => {
    flow.updateWithResponse({
      response: submitInvestment(investment),
      queriesToInvalidate: [],
    })
  }

  const selectInvestmentsForMultiSelect = (token: string, isChecked?: boolean) => {
    if (isChecked) {
      setSelectedTokensForMultiSelect(prevSelections => new Set(prevSelections).add(token))
    } else {
      setSelectedTokensForMultiSelect(prevSelections => {
        const newSelections = new Set(prevSelections)
        newSelections.delete(token)
        return newSelections
      })
    }
  }

  const submitInvestmentsForMultiSelect = () => {
    flow.updateWithResponse({
      response: submitMultipleInvestments([...selectedTokensForMultiSelect]),
      queriesToInvalidate: [],
    })
  }

  if (isLoading || discoveryDataLoading) {
    return <BlockerLoading />
  }

  return (
    <BaseBlocker mainText={title} form={form} onSubmit={() => null} buttons={[]}>
      <Container>
        <InvestmentList
          allInvestmentEntities={allInvestmentEntities}
          searchBarPlaceholderText={search_bar_placeholder_text}
          categories={categories}
          handleInvestmentSelection={isMultiSelect ? selectInvestmentsForMultiSelect : submitInvestmentForSingleSelect}
          multiSelect={isMultiSelect ? { selectedEntityTokens: selectedTokensForMultiSelect } : undefined}
          maxSelections={multi_selection_parameters?.max_selections}
        />
      </Container>
      {isMultiSelect && (
        <Button size="large" onClick={submitInvestmentsForMultiSelect}>
          {multi_selection_parameters?.localized_confirm_cta_text?.translated_value}
        </Button>
      )}
    </BaseBlocker>
  )
}
